import { useRouter } from 'next/router';
import { useEffect, useState, Fragment } from 'react';
import { Grid, Paper, styled as SMUI, Typography } from '@mui/material';
import SearchDetailItem from 'components/search/SearchDetailItem';
import { NextSeo } from 'next-seo';
import * as React from 'react';
import { ROUTES } from '_constants/routes';
import styled from 'styled-components';
import { restoreSessionValue } from '_helpers/storage';

const Item = SMUI(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'left',
  color: theme.palette.text.secondary,
  border: '0px',
  boxShadow: 'none',
}));
const StyledButton = styled.div`
  cursor: pointer;
  max-width: fit-content;
  margin-top: 1em;
  margin-left: 8px;
  @media (max-width: 768px) {
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    display: block;
  }
`;
export default function SearchDetails() {
  const router = useRouter();
  const id = router.query['id'];
  const [searchItem, setSearchItem] = useState(null);
  useEffect(() => {
    if (id) {
      setSearchItem(restoreSessionValue('searchResult'));
    }
  }, [id]);
  const Statements = ({ data }) =>
    Object.entries(data).map(([key, v], i) => (
      <Fragment key={i}>
        <Grid item xs={4}>
          <Item>
            <b>
              {key.includes('DM')
                ? 'Decimal Mark'
                : key.includes('PM')
                ? 'Pseudo Mark'
                : key.includes('GS')
                ? 'Goods and Services'
                : key.includes('CC')
                ? 'Coordinated Class'
                : key}
            </b>
          </Item>
        </Grid>
        <Grid item xs={8}>
          <Item>{v}</Item>
        </Grid>
      </Fragment>
    ));
  const RegistrationDate = ({ events }) => (
    <Grid item xs={4}>
      <Item>
        <b>Registration Date</b>
      </Item>
      <Item>
        {events.find((event) => event.code === 'R.PR')
          ? events.find((event) => event.code === 'R.PR').date
          : new Date().toISOString().slice(0, 10)}
      </Item>
    </Grid>
  );

  const Owner = ({ owner }) => (
    <Grid container spacing={2}>
      {owner?.partyName && (
        <SearchDetailItem
          label="Party Name"
          searchItemValue={owner.partyName}
        />
      )}
      {owner?.partyType && (
        <SearchDetailItem
          label="Party Type"
          searchItemValue={owner.partyType}
        />
      )}
      {owner?.legalEntityTypeCode && (
        <SearchDetailItem
          label="Legal Entity Type"
          searchItemValue={owner.legalEntityTypeCode}
        />
      )}
      <Grid item xs={12}>
        <Item>
          <b>Address</b>
        </Item>
        <Item>
          {owner?.address1 ? owner.address1 : ''}
          {owner?.address2 ? ', ' + owner.address2 : ''}
          {owner?.city ? ', ' + owner.city : ''}
          {owner?.state ? ', ' + owner.state : ''}
          {owner?.postcode ? ', ' + owner.postcode : ''}
        </Item>
      </Grid>
    </Grid>
  );

  return (
    <div className="home search">
      <NextSeo title="Search Details" />
      <div className="table-results">
        <div className="wrapper">
          <Typography
            style={{
              fontFamily: 'MontserratBold',
              fontSize: '14px',
              lineHeight: '24px',
              marginBottom: '1em',
              cursor: 'pointer',
              color: '#03589C',
              paddingLeft: '8px',
            }}
            onClick={() => router.back()}
          >
            Return to Search Results
          </Typography>
          <StyledButton
            className="orange-btn"
            onClick={() => router.push(ROUTES.trademarkRegistrationFiling)}
          >
            Start Trademark Registration
          </StyledButton>
          {searchItem && (
            <div>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Item>
                    <Typography style={{ fontWeight: 'bold', fontSize: '2em' }}>
                      {searchItem.markIdentification}
                    </Typography>
                  </Item>
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                {searchItem.serialNumber && (
                  <SearchDetailItem
                    label="Serial Number"
                    searchItemValue={searchItem.serialNumber}
                  />
                )}
                {searchItem.registrationNumber && (
                  <SearchDetailItem
                    label="Registration Number"
                    searchItemValue={searchItem.registrationNumber}
                  />
                )}
                {searchItem.markIdentification && (
                  <SearchDetailItem
                    label="Word Mark"
                    searchItemValue={searchItem.markIdentification}
                  />
                )}
              </Grid>
              <Grid container spacing={2}>
                {searchItem.statusCode && (
                  <SearchDetailItem
                    label="Status"
                    searchItemValue={searchItem.statusCode}
                  />
                )}
                {searchItem.statusDate && (
                  <SearchDetailItem
                    label="Status Date"
                    searchItemValue={searchItem.statusDate}
                  />
                )}
                {searchItem.filingDate && (
                  <SearchDetailItem
                    label="Filing Date"
                    searchItemValue={searchItem.filingDate}
                  />
                )}
              </Grid>
              <Grid container spacing={2}>
                {searchItem.events && (
                  <RegistrationDate events={searchItem.events} />
                )}
                {searchItem.markDrawingCode && (
                  <SearchDetailItem
                    label="Mark Drawing"
                    searchItemValue={searchItem.markDrawingCode}
                  />
                )}
                {searchItem.attorneyName && (
                  <SearchDetailItem
                    label="Attorney Name"
                    searchItemValue={searchItem.attorneyName}
                  />
                )}
              </Grid>

              {searchItem.caseFileStatements && (
                <Grid container>
                  <Grid item xs={12}>
                    <Item>
                      <h4>Statements</h4>
                    </Item>
                  </Grid>
                  <Statements data={searchItem.caseFileStatements} />
                </Grid>
              )}

              {searchItem.classification && (
                <>
                  <Grid container>
                    <Grid item xs={12}>
                      <Item>
                        <h4>Classification Information</h4>
                      </Item>
                    </Grid>
                  </Grid>
                  {searchItem.classification.map((classificationItem, i) => (
                    <Fragment key={i}>
                      <Grid container>
                        <Grid item xs={12}>
                          <Item>
                            <b>International Class</b>
                          </Item>
                          <Item>{classificationItem.internationalCode}</Item>
                        </Grid>
                      </Grid>
                      <Grid container spacing={2}>
                        {classificationItem.usCode && (
                          <SearchDetailItem
                            label="US Class Codes"
                            searchItemValue={classificationItem.usCode
                              .map((usCode) => `${usCode}`)
                              .join(',')}
                          />
                        )}
                        {classificationItem.statusCode && (
                          <SearchDetailItem
                            label="Class Status Code"
                            searchItemValue={classificationItem.statusCode}
                          />
                        )}
                        {classificationItem.statusDate && (
                          <SearchDetailItem
                            label="Class Status Date"
                            searchItemValue={classificationItem.statusDate}
                          />
                        )}
                      </Grid>
                      <Grid container spacing={2}>
                        {classificationItem.primaryCode && (
                          <SearchDetailItem
                            label="Primary Code"
                            searchItemValue={classificationItem.primaryCode}
                          />
                        )}
                        {classificationItem.firstUseAnywhereDate && (
                          <SearchDetailItem
                            label="First Use Anywhere Date"
                            searchItemValue={
                              classificationItem.firstUseAnywhereDate
                            }
                          />
                        )}
                        {classificationItem.firstUseInCommerceDate && (
                          <SearchDetailItem
                            label="First Use In Commerce Date"
                            searchItemValue={
                              classificationItem.firstUseInCommerceDate
                            }
                          />
                        )}
                      </Grid>
                    </Fragment>
                  ))}
                </>
              )}

              {searchItem.owners && (
                <>
                  <Grid container>
                    <Grid item xs={12}>
                      <Item>
                        <h4>Current Trademark Owners</h4>
                      </Item>
                    </Grid>
                  </Grid>
                  <Owner owner={searchItem.owners[0]} />

                  <Grid container>
                    <Grid item xs={12}>
                      <Item>
                        <h4>Trademark Owner History</h4>
                      </Item>
                    </Grid>
                  </Grid>
                  {searchItem.owners.map((ownerItem, i) => (
                    <Owner key={i} owner={ownerItem} />
                  ))}
                </>
              )}

              {searchItem.correspondenceName && (
                <Grid container>
                  <Grid item xs={12}>
                    <Item>
                      <h4>Correspondences</h4>
                    </Item>
                  </Grid>
                  <Grid item xs={4}>
                    <Item>
                      <b>Name</b>
                    </Item>
                    <Item>{searchItem.correspondenceName}</Item>
                  </Grid>
                  <Grid item xs={8}>
                    <Item>
                      <b>Address</b>
                    </Item>
                    <Item>
                      {searchItem.correspondenceAddress1
                        ? searchItem.correspondenceAddress1
                        : ''}
                      {searchItem.correspondenceAddress2
                        ? ', ' + searchItem.correspondenceAddress2
                        : ''}
                    </Item>
                  </Grid>
                </Grid>
              )}
              {searchItem.events && (
                <>
                  <Grid container>
                    <Grid item xs={12}>
                      <Item>
                        <h4>Trademark Events</h4>
                      </Item>
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item xs={4}>
                      <Item>
                        <b>Event Date</b>
                      </Item>
                    </Grid>
                    <Grid item xs={8}>
                      <Item>
                        <b>Event Description</b>
                      </Item>
                    </Grid>

                    {searchItem.events.map((eventItem, i) => (
                      <Fragment key={i}>
                        <Grid item xs={4}>
                          <Item>{eventItem.date}</Item>
                        </Grid>
                        <Grid item xs={8}>
                          <Item>{eventItem.descriptionText}</Item>
                        </Grid>
                      </Fragment>
                    ))}
                  </Grid>
                </>
              )}
              <StyledButton
                className="orange-btn"
                onClick={() => router.push(ROUTES.trademarkRegistrationFiling)}
              >
                Start Trademark Registration
              </StyledButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
